import { useEffect, useState } from "react";

/**
 * Live OS keyboard-focus state for the host document.
 *
 * Mirrors `apps/extension/src/sidepanel/useHasFocus.ts` (built there for the
 * keyboard footer's focus-awareness, since Chrome provides no API to
 * programmatically transfer focus to an already-open side panel —
 * docs/prd-keyboard.md §2.1). `packages/client-ui` cannot import that file —
 * dependencies only run app -> package here, never the reverse — so this is
 * a deliberate mirror of the same signal (`document.hasFocus()` seed +
 * `window` focus/blur), not a third focus-tracking mechanism invented for
 * `ClipboardScreen`'s own use (round 3 D5 fix round: re-masking every
 * `ClipCard` on focus loss, not just on `document.hidden`).
 *
 * Initializes from `document.hasFocus()` and tracks `window` focus/blur.
 */
export function useHasFocus(): boolean {
  const [focused, setFocused] = useState(() => document.hasFocus());
  useEffect(() => {
    const onFocus = () => setFocused(true);
    const onBlur = () => setFocused(false);
    window.addEventListener("focus", onFocus);
    window.addEventListener("blur", onBlur);
    // Re-sync in case focus changed between the initial render and effect commit.
    setFocused(document.hasFocus());
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("blur", onBlur);
    };
  }, []);
  return focused;
}
